/*
  Uma Musume: Pretty Derby Catalog Integration
  - POST-based API with JSON array payload
  - Multi-proxy fallback for CORS handling
  - Card metadata: POST /card/pageEq?gameId=4
  - Account search: POST /group/listByCards?gameId=4
  - Selection tracking with count badges
  - Server filtering support
*/
(function(){
  /* ====== API Sources Declaration ====== */
  const API_SOURCES = {
    'uma01': { name: 'Uma Musume API', enabled: true }
  };
  
  const escapeHtml = s => String(s||'').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  
  // API Configuration
  const BASE_API = 'https://www.naonaocs.cn/game-api';
  const GAME_ID = 4; // Uma Musume game ID
  const CONTAINER_ID = 'uma01';
  
  // CORS Proxy List (try in order)
  const PROXY_LIST = [
    'https://corsproxy.io/?',
    'https://api.codetabs.com/v1/proxy?quest=',
    'https://api.allorigins.win/raw?url=',
    'https://thingproxy.freeboard.io/fetch/'
  ];
  let proxyIndex = 0;
  
  // State Management
  let cardMap = {}; // shortName -> card object
  let cardList = []; // array of all cards
  let selected = {}; // { shortName: { count, name, img, originalName } }
  
  // Settings & Translations Initialization
  let umaSettings = {translations_json: '{}', images_json: '{}', api_toggles: {}};
  try {
    const el = document.getElementById('catalog-uma-settings');
    if (el) umaSettings = JSON.parse(el.textContent);
  } catch(e) { 
    console.error('[UMA] Failed to parse settings:', e);
    umaSettings = {translations_json: '{}', images_json: '{}', api_toggles: {}}; 
  }
  
  // console.log('[UMA] Raw settings from Liquid:', umaSettings);
  // console.log('[UMA] API toggles received:', umaSettings.api_toggles);
  
  // Override API_SOURCES enabled state from admin settings
  if (umaSettings.api_toggles) {
    Object.keys(API_SOURCES).forEach(apiId => {
      const oldState = API_SOURCES[apiId].enabled;
      if (umaSettings.api_toggles.hasOwnProperty(apiId)) {
        API_SOURCES[apiId].enabled = umaSettings.api_toggles[apiId] === true;
        // console.log(`[UMA] API '${apiId}': ${oldState} → ${API_SOURCES[apiId].enabled} (admin setting: ${umaSettings.api_toggles[apiId]})`);
      }
    });
  }
  
  // console.log('[UMA] ═══════════════════════════════════════════');
  // console.log('[UMA] FINAL API SOURCES STATUS:');
  Object.entries(API_SOURCES).forEach(([id, api]) => {
    // console.log(`[UMA]   ${id}: ${api.enabled ? '✅ ENABLED' : '❌ DISABLED'} (${api.name})`);
  });
  // console.log('[UMA] ═══════════════════════════════════════════');
  
  const enableApiCalls = API_SOURCES.uma01.enabled;

  let nameTranslations = {}, nameTranslationsMap = {};
  try {
    nameTranslations = JSON.parse(umaSettings.translations_json || '{}');
    Object.entries(nameTranslations).forEach(([key, val]) => {
      key.split('|').map(k => k.trim()).forEach(k => { if (k) nameTranslationsMap[k] = val; });
    });
    // console.log('[UMA] Translations loaded:', Object.keys(nameTranslationsMap).length, 'entries');
  } catch(e) { 
    console.warn('[UMA] Failed to parse translations:', e);
    nameTranslations = {}; 
    nameTranslationsMap = {}; 
  }

  let customImages = {}, customImagesMap = {};
  try {
    customImages = JSON.parse(umaSettings.images_json || '{}');
    Object.entries(customImages).forEach(([key, val]) => {
      key.split('|').map(k => k.trim()).forEach(k => { if (k) customImagesMap[k] = val; });
    });
    // console.log('[UMA] Custom images loaded:', Object.keys(customImagesMap).length, 'entries');
  } catch(e) { 
    console.warn('[UMA] Failed to parse custom images:', e);
    customImages = {}; 
    customImagesMap = {}; 
  }

  // Helper Functions
  const translateName = name => nameTranslationsMap[name] || name;
  const getCustomImage = name => customImagesMap[name] || null;
  
  const translateServer = (svr) => {
    const serverMap = {
      1: 'America',
      2: 'Europe',
      3: 'Asia',
      4: 'TW/HK/MO'
    };
    return serverMap[svr] || `Server ${svr}`;
  };

  // Fetch with multi-proxy fallback
  async function fetchWithProxy(url, options = {}) {
    // console.log('[UMA] Fetching:', url);
    
    // Try direct fetch first
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        // console.log('[UMA] Direct fetch successful');
        return response;
      }
    } catch (err) {
      console.warn('[UMA] Direct fetch failed:', err.message);
    }
    
    // Try proxies in order
    for (let i = 0; i < PROXY_LIST.length; i++) {
      const idx = (proxyIndex + i) % PROXY_LIST.length;
      const proxyUrl = PROXY_LIST[idx] + encodeURIComponent(url);
      
      try {
        // console.log(`[UMA] Trying proxy ${idx + 1}/${PROXY_LIST.length}:`, PROXY_LIST[idx]);
        const response = await fetch(proxyUrl, options);
        if (response.ok) {
          // console.log('[UMA] Proxy fetch successful');
          proxyIndex = idx; // Remember working proxy
          return response;
        }
      } catch (err) {
        console.warn(`[UMA] Proxy ${idx + 1} failed:`, err.message);
      }
    }
    
    throw new Error('All fetch attempts failed (direct + all proxies)');
  }

  // Extract records from various response formats
  function extractRecordsFromResponse(json) {
    if (!json) return null;
    if (json.data?.records) return json.data.records;
    if (Array.isArray(json.data)) return json.data;
    if (json.records) return json.records;
    return null;
  }

  // UI Update Functions
  const scrollToResults = () => document.querySelector('#results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  
  function updateSelectedUI() {
    const totalCount = Object.values(selected).reduce((sum, sel) => sum + (sel.count || 0), 0);
    const selectedCountEl = document.querySelector("#selectedCount");
    if (selectedCountEl) selectedCountEl.textContent = totalCount;
    
    // Update search button state
    const searchBtn = document.querySelector("#searchBtn");
    if (searchBtn) {
      searchBtn.disabled = totalCount === 0;
      searchBtn.textContent = totalCount > 0 ? `🔍 Search (${totalCount} selected)` : '🔍 Search';
    }
  }

  function updateThumbDisplay(thumb, shortName) {
    const count = selected[shortName]?.count || 0;
    const existingBadge = thumb.querySelector('.count-badge');
    
    if (count > 0) {
      thumb.classList.add('selected');
      if (existingBadge) {
        existingBadge.textContent = count;
      } else {
        const badge = document.createElement('div');
        badge.className = 'count-badge';
        badge.textContent = count;
        thumb.querySelector('.thumb-img-container').appendChild(badge);
      }
    } else {
      thumb.classList.remove('selected');
      if (existingBadge) existingBadge.remove();
    }
  }

  function clearSelection() {
    selected = {};
    document.querySelectorAll('.thumb').forEach(thumb => {
      thumb.classList.remove('selected');
      const badge = thumb.querySelector('.count-badge');
      if (badge) badge.remove();
    });
    updateSelectedUI();
    
    // Clear results
    const resultsEl = document.querySelector("#results");
    if (resultsEl) resultsEl.innerHTML = '';
  }

  function showToast(message, duration = 3000) {
    const toast = document.querySelector("#toast");
    if (!toast) return;
    
    const toastText = toast.querySelector('.toast-content span');
    if (toastText && message) toastText.textContent = message;
    
    toast.classList.remove('hidden');
    toast.offsetHeight; // Force reflow
    toast.classList.add('show');
    
    if (duration > 0) {
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.classList.add('hidden'), 300);
      }, duration);
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => showToast('ID copied! Please share this with customer service to get price quotation.'))
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showToast('ID copied! Please share this with customer service to get price quotation.');
    } catch(e) {
      console.error('[UMA] Copy failed:', e);
    }
    document.body.removeChild(ta);
  }

  // Load Card Metadata
  async function loadCardData() {
    const container = document.querySelector("#filters");
    if (!container) return;
    
    // Show API status banner if disabled
    const disabledApis = Object.entries(API_SOURCES).filter(([id, api]) => !api.enabled).map(([id, api]) => api.name);
    if (disabledApis.length > 0) {
      // console.log('[UMA] Some APIs are disabled - showing warning banner');
      const searchSection = document.querySelector("#uma01");
      if (searchSection) {
        const banner = document.createElement('div');
        banner.style.cssText = 'background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; padding: 12px 16px; margin: 16px 0; color: #92400e; font-weight: 500; text-align: center;';
        banner.innerHTML = `⚠️ API calls are currently disabled. Search functionality is temporarily unavailable. Please contact Gachamacha support for assistance.`;
        searchSection.insertBefore(banner, searchSection.firstChild);
      }
    }
    
    if (!enableApiCalls) {
      // console.log('[UMA] ❌ Skipping Uma Musume API (disabled)');
      container.innerHTML = '<div class="no-data">⚠️ API calls are currently disabled.<br><br>Please contact Gachamacha support for assistance.</div>';
      return;
    }
    
    // console.log('[UMA] ✅ Loading Uma Musume API');
    container.innerHTML = '<div class="loading">Loading cards...</div>';
    
    const url = `${BASE_API}/card/pageEq?pageNum=1&pageSize=1000`;
    const headers = {
      'Accept': 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      'Referer': 'https://www.naonaocs.cn/smn',
      'Origin': 'https://www.naonaocs.cn'
    };
    
    try {
      const response = await fetchWithProxy(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ gameId: GAME_ID })
      });
      
      const json = await response.json();
      // console.log('[UMA] Card metadata response:', json);
      
      if (json.code !== 0) {
        throw new Error(json.message || 'API returned error code');
      }
      
      const records = extractRecordsFromResponse(json);
      if (!records || !records.length) {
        throw new Error('No card data found in response');
      }
      
      cardList = records;
      cardMap = {};
      records.forEach(card => {
        if (card.shortName) cardMap[card.shortName] = card;
      });
      
      // console.log(`[UMA] Loaded ${cardList.length} cards`);
      renderFilterUI();
      
    } catch (error) {
      console.error('[UMA] Failed to load card data:', error);
      container.innerHTML = '<div class="no-data">Failed to load card data. Please refresh the page.</div>';
    }
  }

  // Render Filter UI
  function renderFilterUI() {
    const container = document.querySelector("#filters");
    if (!container) return;
    
    container.innerHTML = '';
    
    if (!cardList.length) {
      container.innerHTML = '<div class="no-data">No cards available.</div>';
      return;
    }
    
    // Sort by sort field (descending)
    const sortedCards = cardList.slice().sort((a, b) => (b.sort || 0) - (a.sort || 0));
    
    sortedCards.forEach(card => {
      const shortName = card.shortName || `ID${card.id}`;
      const displayName = translateName(card.cardName || shortName);
      const customImg = getCustomImage(card.cardName);
      const displayImg = customImg || card.cardPicture || `https://via.placeholder.com/78?text=${shortName}`;
      
      const thumb = document.createElement('div');
      thumb.className = 'thumb';
      thumb.dataset.short = shortName;
      thumb.title = displayName;
      
      thumb.innerHTML = `
        <div class="thumb-img-container">
          <img alt="${escapeHtml(displayName)}" 
               src="${escapeHtml(displayImg)}" 
               loading="lazy"
               onerror="this.src='https://via.placeholder.com/78/CCCCCC/666?text=UMA'"/>
        </div>
        <div class="label">${escapeHtml(displayName)}</div>
      `;
      
      thumb.onclick = (e) => {
        const short = thumb.dataset.short;
        
        // Shift+click to decrement
        if (e.shiftKey) {
          if (selected[short] && selected[short].count > 0) {
            selected[short].count--;
            if (selected[short].count === 0) {
              delete selected[short];
            }
          }
        } else {
          // Normal click to increment
          if (!selected[short]) {
            selected[short] = {
              count: 0,
              name: displayName,
              img: displayImg,
              originalName: card.cardName
            };
          }
          selected[short].count++;
        }
        
        updateThumbDisplay(thumb, short);
        updateSelectedUI();
      };
      
      container.appendChild(thumb);
    });
    
    updateSelectedUI();
  }

  // Build Search Payload
  function buildSearchPayload() {
    const payload = [];
    
    Object.keys(selected).forEach(shortName => {
      const sel = selected[shortName];
      if (sel.count > 0) {
        payload.push({
          cardId: shortName,
          num: sel.count
        });
      }
    });
    
    return payload;
  }

  // Perform Search
  async function performSearch() {
    const payload = buildSearchPayload();
    
    if (payload.length === 0) {
      showToast('Please select at least one card', 2000);
      return;
    }
    
    // console.log('[UMA] Search payload:', JSON.stringify(payload, null, 2));
    
    const resultsEl = document.querySelector("#results");
    if (!resultsEl) return;
    
    // Show searching toast and scroll to results
    showToast('🔍 Searching for accounts...', 2000);
    resultsEl.innerHTML = '<div class="loading">🔍 Searching for accounts...</div>';
    scrollToResults();
    
    const pageSize = 20;
    const pageNum = 1;
    const url = `${BASE_API}/group/listByCards?gameId=${GAME_ID}&pageNum=${pageNum}&pageSize=${pageSize}&sortOrder=desc`;
    
    // console.log('[UMA] Search URL:', url);
    
    try {
      const response = await fetchWithProxy(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'Referer': 'https://www.naonaocs.cn/smn',
          'Origin': 'https://www.naonaocs.cn'
        },
        body: JSON.stringify(payload)
      });
      
      const json = await response.json();
      // console.log('[UMA] Search response:', json);
      
      if (json.code !== 0) {
        throw new Error(json.message || 'Search failed');
      }
      
      const accounts = extractRecordsFromResponse(json);
      if (!accounts) {
        throw new Error('No accounts found in response');
      }
      
      // Apply server filter if selected
      const selectedServer = document.querySelector("#serverSelect")?.value;
      const filteredAccounts = selectedServer 
        ? accounts.filter(acc => String(acc.svr) === selectedServer)
        : accounts;
      
      // console.log(`[UMA] Found ${accounts.length} accounts, ${filteredAccounts.length} after server filter`);
      renderResults(filteredAccounts);
      
    } catch (error) {
      console.error('[UMA] Search failed:', error);
      resultsEl.innerHTML = `<div class="no-data">Search failed: ${escapeHtml(error.message)}</div>`;
    }
  }

  // Render Results
  function renderResults(accounts) {
    const resultsEl = document.querySelector("#results");
    if (!resultsEl) return;
    
    if (accounts.length === 0) {
      resultsEl.innerHTML = '<div class="no-data">No accounts found matching your criteria.</div>';
      return;
    }
    
    resultsEl.innerHTML = `<div class="results-header">Found ${accounts.length} account${accounts.length !== 1 ? 's' : ''}</div>`;
    
    accounts.forEach(account => {
      const card = createAccountCard(account);
      resultsEl.appendChild(card);
    });
  }

  // Create Account Card
  function createAccountCard(account) {
    const cardDiv = document.createElement('div');
    cardDiv.className = 'result-card';
    
    const accountId = account.account || account.id || 'N/A';
    const sl = account.sl !== undefined && account.sl !== null ? account.sl : 'N/A';
    const carats = account.day !== undefined && account.day !== null ? account.day : 'N/A';
    
    // Parse cards string (format: "S30028-S30017-S30029") and count duplicates
    const cardCodes = (account.cards || '').split('-').filter(Boolean);
    
    // Count occurrences of each card
    const cardCounts = {};
    cardCodes.forEach(code => {
      cardCounts[code] = (cardCounts[code] || 0) + 1;
    });
    
    // Build character grid HTML with counts
    const characterGridHtml = Object.keys(cardCounts).map(code => {
      const card = cardMap[code];
      const name = card ? translateName(card.cardName) : code;
      const customImg = card ? getCustomImage(card.cardName) : null;
      const img = customImg || (card ? card.cardPicture : `https://via.placeholder.com/75?text=${code}`);
      const count = cardCounts[code];
      
      return `
        <div class="char">
          <img src="${escapeHtml(img)}" 
               alt="${escapeHtml(name)}" 
               onerror="this.src='https://via.placeholder.com/75/CCCCCC/666?text=UMA'"/>
          <div class="cname">${escapeHtml(name)}</div>
          ${count > 1 ? `<div class="cnum">x${escapeHtml(String(count))}</div>` : ''}
        </div>
      `;
    }).join('');
    
    cardDiv.innerHTML = `
      <div class="result-header">
        <div class="result-id">Account: ${escapeHtml(accountId)}</div>
        <button class="copy-btn" onclick="window.umaCopyToClipboard('${escapeHtml(accountId)}')">Copy ID</button>
      </div>
      <div class="result-body">
        <div class="result-info">Server: Global | SL: ${escapeHtml(sl)} | Carats: ${escapeHtml(carats)}</div>
        <div class="result-main">
          <div class="chars-row">
            ${characterGridHtml}
          </div>
        </div>
      </div>
    `;
    
    return cardDiv;
  }

  // Initialize
  function init() {
    // console.log('[UMA] Initializing Uma Musume catalog...');
    
    // Expose copy function globally for onclick handlers
    window.umaCopyToClipboard = copyToClipboard;
    
    // Attach event listeners
    const searchBtn = document.querySelector("#searchBtn");
    if (searchBtn) {
      searchBtn.addEventListener('click', performSearch);
    }
    
    const clearBtn = document.querySelector("#clearBtn");
    if (clearBtn) {
      clearBtn.addEventListener('click', clearSelection);
    }
    
    // Load card data
    loadCardData();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
